import { useCallback, useEffect, useState } from 'react';

import { getNavMeshInfo, isNavMeshAvailable, NavMeshEventEmitter } from '@/editor/core/actions/navmeshActions';

/**
 * Custom hook to provide reactive NavMesh state.
 * Uses event-driven updates instead of polling for better performance.
 */
export function useNavMeshState() {
  const [navMeshState, setNavMeshState] = useState(() => ({
    available: false, // Start with false to avoid showing error during initialization
    hasNavMesh: false,
    isDebugEnabled: false
  }));

  const updateState = useCallback((): boolean => {
    const available = isNavMeshAvailable();
    setNavMeshState({
      available,
      ...getNavMeshInfo()
    });
    return available;
  }, []);

  useEffect(() => {
    // Listen for NavMesh state changes via events
    const emitter = NavMeshEventEmitter.getInstance();

    const handleNavMeshChange = () => updateState();
    const handleDebugToggle = () => updateState();

    emitter.addEventListener('navmesh-changed', handleNavMeshChange);
    emitter.addEventListener('navmesh-debug-toggled', handleDebugToggle);

    // Check periodically until world/navigation server is available
    let retryCount = 0;
    const maxRetries = 50; // 5 seconds max (50 * 100ms)

    const checkInterval = setInterval(() => {
      const available = updateState();
      retryCount++;

      if (available || retryCount >= maxRetries) {
        clearInterval(checkInterval);
      }
    }, 100);

    return () => {
      emitter.removeEventListener('navmesh-changed', handleNavMeshChange);
      emitter.removeEventListener('navmesh-debug-toggled', handleDebugToggle);
      clearInterval(checkInterval);
    };
  }, [updateState]);

  return { ...navMeshState, forceUpdate: updateState };
}
